import React from 'react';
import { useFormContext } from 'react-hook-form';
import Accordion from '../NavTab/Accordion';
import Field from '../Form/Fields/Field';

const findFieldByName = (systemConfig, fieldKey) => {
  const [sectionId, groupId, fieldId] = fieldKey.split('_');

  const section = systemConfig.find(s => s.id === sectionId);
  if (!section) return null;

  const findFieldInGroups = (groups) => {
    for (const group of groups) {
      for (const field of group.fields || []) {
        const currentFieldKey = `${sectionId}_${group.id}_${field.id}`;
        if (currentFieldKey === fieldKey) {
          return field;
        }
      }

      if (group.groups) {
        const result = findFieldInGroups(group.groups);
        if (result) return result;
      }
    }
    return null;
  };

  return findFieldInGroups(section.groups);
};

const useCheckDependsRecursive = (systemConfig) => {
  const { watch } = useFormContext();

  const checkDepends = (depends) => {
    if (!depends) return true;

    const currentValue = watch(depends.field);
    const isVisible = currentValue === depends.value;

    if (!isVisible) return false;

    const parentField = findFieldByName(systemConfig, depends.field);
    if (parentField?.depends) {
      return checkDepends(parentField.depends);
    }

    return true;
  };

  return checkDepends;
};


const GroupRenderer = ({ sectionId, group, systemConfig, sectionIcon }) => {
  const checkDepends = useCheckDependsRecursive(systemConfig);

  const show = checkDepends(group.depends);
  if (!show) return null;


  return (
    <Accordion
      key={`${sectionId}_${group.id}`}
      id={group.id}
      title={group.label}
      icon={group.icon ?? sectionIcon}
    >
      <div className="space-y-3">
        {(group.fields || []).map(field => {
          const fieldKey = `${sectionId}_${group.id}_${field.id}`;
          const fieldShow = checkDepends(field.depends);

          return (
            fieldShow && (
              <React.Fragment key={fieldKey}>
                {field.Cell ? (
                  field.Cell()
                ) : (
                  <Field
                    label={field.label}
                    component={field.type}
                    name={fieldKey}
                    placeholder={field.placeholder || ''}
                      options={field?.options}
                      multiple={field?.multiple}
                      helperMessage={field?.helperMessage}
                  />
                )}
              </React.Fragment>
            )
          );
        })}

        {(group.groups || []).map(subgroup => {
          if (checkDepends(subgroup.depends)) {
            return (
              <GroupRenderer
                key={`${sectionId}_${subgroup.id}`}
                sectionId={sectionId}
                group={subgroup}
                systemConfig={systemConfig}
                sectionIcon={sectionIcon}
              />
            );
          }
          return null;
        })}
      </div>
    </Accordion>
  );
};

const SettingSection = ({ section, systemConfig }) => {
  if (!section?.groups || section.groups.length === 0) {
    return <p>No setting group available.</p>;
  }

  return (
    <div className="setting-section space-y-4">
      {section.groups.map(group => (
        <GroupRenderer
          key={`${section.id}_${group.id}`}
          sectionId={section.id}
          group={group}
          systemConfig={systemConfig}
          sectionIcon={section.icon}
        />
      ))}
    </div>
  );
};

export default SettingSection;
